<template>
    <div>
        <div class="p-grid">
            <div class="p-col-12">
                <div class="card">
                    <DataTable ref="dt" :value="items.data" :lazy="true" data-key="id" :paginator="true" :rows="10"
                        :loading="loading" :total-records="items.total" filter-display="menu"
                        :global-filter-fields="['paket_pengadaan.nama_paket_pengadaan']"
                        v-model:filters="filters"
                        paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        :rows-per-page-options="[5, 10, 25]"
                        current-page-report-template="Menampilkan data ke {first} sampai {last} dari total {totalRecords} data"
                        @page="onPage($event)">
                        <template #header>
                            <div class="table-header">
                                <h5 class="p-m-0 text-center">
                                    Dokumen Pengadaan Berjalan !
                                </h5>
                                <div class="flex justify-content-between">
                                    <Button type="button" icon="pi pi-filter-slash" label="Clear"
                                        class="p-button-outlined" @click="clearFilter()" />
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search" />
                                        <InputText v-model="filters['global'].value"
                                            placeholder="Cari..." />
                                    </span>
                                </div>
                            </div>
                        </template>

                        <Column field="paket_pengadaan.isi_doc" header="Nama Paket" :sortable="true">
                            <template #body="slotProps">
                                {{ slotProps.data.isi_doc }}
                            </template>
                        </Column>
                        <Column field="tahap.nama_tahap" header="Tahap" :sortable="true">
                            <template #body="slotProps">
                                {{ slotProps.data.tahap?.nama_tahap }}
                            </template>
                        </Column>
                        <Column field="status" header="Status" :sortable="true">
                            <template #body="slotProps">
                                <div class="flex align-items-center">
                                    <span :class="slotProps.data.lock_status === 'SEALED' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'" 
                                          class="px-2 py-1 rounded-md text-xs font-medium mr-2">
                                        {{ slotProps.data.lock_status === 'SEALED' ? 'TERSEGEL' : 'TERBUKA' }}
                                    </span>
                                    {{ slotProps.data.status }}
                                    <div v-if="slotProps.data.lock_status === 'SEALED' && slotProps.data.unlock_at" 
                                         class="ml-2 p-1 bg-yellow-50 border border-yellow-200 rounded text-xs">
                                        <i class="pi pi-clock mr-1"></i>
                                        Dapat dibuka pada: {{ new Date(slotProps.data.unlock_at).toLocaleString() }}
                                    </div>
                                </div>
                            </template>
                        </Column>
                        <Column header="Aksi" style="width: 100px;">
                            <template #body="slotProps">
                                <div class="flex flex-col gap-1">
                                    <!-- First row: Main action buttons -->
                                    <div class="flex gap-1">
                                        <!-- Always show Lihat button -->
                                        <Link :href="route('dokumen_pengadaan.detail', slotProps.data.id)">
                                            <Button icon="pi pi-eye" class="p-button-info p-button-sm" v-tooltip.top="'Lihat'" />
                                        </Link>
                                        
                                        <!-- Always show Jadwal button -->
                                        <Button icon="pi pi-calendar" class="p-button-warning p-button-sm" 
                                            v-tooltip.top="'Jadwal'" 
                                            @click="showJadwalPopover(slotProps.data.id)" />
                                        
                                        <!-- History button - Show if has evaluasi ulang history -->
                                        <Button v-if="slotProps.data.has_evaluasi_ulang_history"
                                            icon="pi pi-history" 
                                            class="p-button-secondary p-button-sm"
                                            v-tooltip.top="'Lihat Riwayat Evaluasi'"
                                            @click="showHistoricEvaluasi(slotProps.data)" />
                                        
                                        <!-- Hide other buttons if status is 'ditolak' -->
                                        <template v-if="slotProps.data.status !== 'ditolak'">
                                            <Link :href="route('dokumen-pengadaan.pengajuan', slotProps.data.id)" 
                                                  @click.prevent="slotProps.data.lock_status === 'SEALED' ? 
                                                      showSealedWarning() : null"
                                                  :class="{'pointer-events-none': slotProps.data.lock_status === 'SEALED'}">
                                                <Button icon="pi pi-list" class="p-button-success p-button-sm" 
                                                       v-tooltip.top="'Detail'"
                                                       :disabled="slotProps.data.lock_status === 'SEALED'" />
                                            </Link>
                                            <Button icon="pi pi-pencil" class="p-button-info p-button-sm" 
                                                v-tooltip.top="'Edit Jadwal'" 
                                                @click="openEditJadwalModal(slotProps.data.id)" />
                                            <Link :href="route('subject-penjelasan.index', slotProps.data.id)">
                                                <Button icon="pi pi-comments" class="p-button-info p-button-sm" 
                                                       v-tooltip.top="'Penjelasan'" />
                                            </Link>
                                            <Button v-if="slotProps.data.lock_status === 'SEALED'" 
                                                icon="pi pi-unlock" class="p-button-danger p-button-sm"
                                                v-tooltip.top="'Buka Segel'"
                                                @click="tryUnlock(slotProps.data)" />
                                        </template>
                                    </div>
                                    
                                    <!-- Second row: Pembatalan actions -->
                                    <div class="flex gap-1" v-if="isPokja && slotProps.data.status !== 'ditolak'">
                                        <Button v-if="isPokja && slotProps.data.status !== 'ditolak' && (slotProps.data.pokja_summary?.pokja_count || 0) > 1"
                                            icon="pi pi-user-edit"
                                            class="p-button-warning p-button-sm"
                                            v-tooltip.top="'Propose Pembatalan'"
                                            @click="openProposePembatalan(slotProps.data)" />
                                        <Button v-if="slotProps.data.can_batal"
                                            icon="pi pi-times"
                                            class="p-button-danger p-button-sm"
                                            v-tooltip.top="'Batal'"
                                            @click="batal(slotProps.data)" />
                                    </div>
                                </div>
                            </template>
                        </Column>
                        <template #empty>
                            Belum ada Data!
                        </template>
                    </DataTable>
                </div>
            </div>
        </div>
        <Dialog v-model:visible="jadwalDialog" header="Jadwal Pengadaan" :style="{ width: '50vw' }" :modal="true">
            <div v-if="loadingJadwal" class="flex justify-content-center">
                <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
            </div>
            <div v-else-if="selectedJadwal.length === 0" class="p-4 text-center">
                Tidak ada jadwal tersedia
            </div>
            <div v-else class="p-datatable p-component">
                <table class="p-datatable-table">
                    <thead class="p-datatable-thead">
                        <tr>
                            <th>Nama Jadwal</th>
                            <th>Waktu Mulai</th>
                            <th>Waktu Selesai</th>
                        </tr>
                    </thead>
                    <tbody class="p-datatable-tbody">
                        <tr v-for="jadwal in selectedJadwal" :key="jadwal.id" class="p-datatable-row"
                            :class="{ 'active-jadwal': jadwal.isActive }">
                            <td>{{ formatJadwalDisplay(jadwal) }}</td>
                            <td>{{ new Date(jadwal.waktu_mulai).toLocaleString() }}</td>
                            <td>{{ new Date(jadwal.waktu_selesai).toLocaleString() }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </Dialog>
        <Dialog v-model:visible="decryptDialog" header="Konfirmasi Buka Segel" :style="{ width: '30vw' }" :modal="true">
            <div class="p-4">
                <p class="mb-4">Apakah Anda yakin ingin membuka segel dokumen ini?</p>
                
                <div class="p-3 bg-yellow-50 border border-yellow-200 rounded mb-4">
                    <h3 class="font-bold text-yellow-800 mb-2">Informasi Penting</h3>
                    <p class="mb-2 text-yellow-700">
                        <i class="pi pi-exclamation-triangle mr-2"></i>
                        Dokumen ini seharusnya terbuka pada: <strong>{{ selectedDocumentUnlockTime }}</strong>
                    </p>
                    <p class="mb-2 text-yellow-700">
                        <i class="pi pi-info-circle mr-2"></i>
                        Tindakan ini akan mendekripsi data sensitif sebelum waktunya.
                    </p>
                    <p class="text-yellow-700">
                        <i class="pi pi-shield mr-2"></i>
                        Pembukaan segel akan dicatat dalam log sistem dan tidak dapat dibatalkan.
                    </p>
                </div>
                
                <div class="flex justify-content-end">
                    <Button label="Batal" icon="pi pi-times" class="p-button-text" @click="decryptDialog = false" />
                    <Button label="Buka Segel" icon="pi pi-unlock" class="p-button-danger" 
                        @click="decryptDocument" :loading="decryptLoading" />
                </div>
            </div>
        </Dialog>
        <Dialog v-model:visible="editJadwalModalOpen" header="Edit Jadwal Pengadaan" :style="{ width: '35vw' }" :modal="true">
            <div class="p-4">
                <div v-if="loadingEditJadwal" class="flex justify-content-center my-8">
                    <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
                </div>
                <div v-else>
                    <div class="mb-6">
                        <label for="jadwal-selection" class="block text-sm font-medium text-gray-700 mb-1">Pilih Jadwal</label>
                        <select
                            id="jadwal-selection"
                            v-model="selectedJadwalId"
                            @change="onEditJadwalSelected"
                            class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        >
                            <option value="" disabled>Pilih Jadwal</option>
                            <option
                                v-for="jadwal in editJadwalList"
                                :key="jadwal.id"
                                :value="jadwal.id"
                                :disabled="isJadwalInPast(jadwal)"
                            >
                                {{ (jadwal.label || jadwal.nama_jadwal || jadwal.nama || jadwal.kode_jadwal) }}
                                ({{ formatDateTime(jadwal.waktu_mulai) }} - {{ formatDateTime(jadwal.waktu_selesai) }})
                                <span v-if="isJadwalInPast(jadwal)">(Sudah lewat)</span>
                            </option>
                        </select>
                        <p class="text-sm text-gray-500 mt-1">Jadwal yang sudah lewat tidak dapat diedit.</p>
                    </div>
                    <div v-if="editJadwalForm" class="space-y-4">
                        <div v-if="isExtendSelesaiOnlyJadwal" class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                            <p class="text-sm text-yellow-700">
                                <strong>Perhatian:</strong> Untuk jadwal ini, hanya waktu selesai yang dapat diubah dan harus diperpanjang (waktu lebih lama).
                            </p>
                        </div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Waktu Mulai</label>
                                <Calendar
                                    v-model="editJadwalForm.waktu_mulai"
                                    showTime
                                    hourFormat="24"
                                    class="w-full"
                                    :disabled="isExtendSelesaiOnlyJadwal"
                                    :class="{'opacity-70 cursor-not-allowed': isExtendSelesaiOnlyJadwal}"
                                />
                                <p v-if="isExtendSelesaiOnlyJadwal" class="text-xs text-gray-500 mt-1">Waktu mulai tidak dapat diubah untuk jadwal ini</p>
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Waktu Selesai</label>
                                <Calendar
                                    v-model="editJadwalForm.waktu_selesai"
                                    showTime
                                    hourFormat="24"
                                    class="w-full"
                                />
                            </div>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Tempat</label>
                            <input
                                type="text"
                                v-model="editJadwalForm.tempat"
                                class="mt-1 block w-full border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm"
                            />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Keterangan</label>
                            <Textarea
                                v-model="editJadwalForm.keterangan"
                                rows="3"
                                class="mt-1 block w-full border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <template #footer>
                <div class="flex justify-end space-x-3">
                    <Button label="Batal" class="p-button-text" @click="closeEditJadwalModal" :disabled="submittingEditJadwal" />
                    <Button label="Simpan" class="p-button-warning" @click="submitEditJadwal" :disabled="!editJadwalForm || submittingEditJadwal" :loading="submittingEditJadwal" />
                </div>
            </template>
        </Dialog>
        <Toast />
        
        <!-- Konfirmasi Batal Dialog -->
        <Dialog v-model:visible="konfirmBatalDialog" :header="batalHeader" :style="{width: '50vw'}" :modal="true">
            <div class="p-4">
                <div class="mb-4">
                    <p>Apakah Anda yakin ingin membatalkan dokumen pengadaan ini?</p>
                    <div class="mt-2 text-yellow-600">
                        <i class="pi pi-info-circle mr-2"></i>
                        <span>Tindakan ini tidak dapat dibatalkan. Harap isi keterangan dan tanda tangan untuk melanjutkan.</span>
                    </div>
                </div>
                
                <!-- Signature Section (minimal) -->
                <div class="mb-4">
                    <div class="mb-2 text-sm text-gray-700">Nama: {{ $page.props.user?.fullname || 'N/A' }} | NIP: {{ $page.props.user?.identity || 'N/A' }}</div>
                    <div class="border rounded-lg p-2 bg-gray-100">
                        <VueSignaturePad class="border-2 rounded-lg p-2 bg-white"
                            ref="signaturePad"
                            width="500px" height="250px"
                            :options="padOptions" 
                        />
                    </div>
                    <div class="mt-2 flex space-x-2">
                        <button 
                            @click="clearSignature"
                            class="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Hapus Tanda Tangan
                        </button>
                    </div>
                </div>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                        Keterangan Pembatalan
                    </label>
                    <textarea v-model="catatan" rows="4" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder="Masukkan catatan..."></textarea>
                </div>
            </div>
            
            <template #footer>
                <div class="flex flex-col space-y-3">
                    <div class="flex justify-end">
                        <Button
                            @click="confirmBatal"  
                            :disabled="!isFormValid"
                            class="p-button-danger"
                            label="Konfirmasi Pembatalan"
                            :loading="submittingBatal"
                        />
                    </div>
                    <div class="flex justify-end">
                        <Button
                            @click="tutupBatal"
                            class="p-button-text"
                            label="Batal"
                        />
                    </div>
                </div>
            </template>
        </Dialog>

        <!-- Propose Pembatalan Dialog -->
        <Dialog v-model:visible="proposeDialog" header="Propose Pembatalan" :style="{width: '40vw'}" :modal="true">
            <div class="p-4">
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Data Diri</label>
                    <div class="border rounded-lg p-4 bg-gray-50 text-sm">
                        <table class="mb-2 w-full text-sm">
                            <tr>
                                <td class="font-bold">Nama</td>
                                <td>:</td>
                                <td>{{ $page.props.user?.fullname || 'N/A' }}</td>
                            </tr>
                            <tr>
                                <td class="font-bold">NIP</td>
                                <td>:</td>
                                <td>{{ $page.props.user?.identity || 'N/A' }}</td>
                            </tr>
                            <tr>
                                <td class="font-bold">Jabatan</td>
                                <td>:</td>
                                <td>POKJA</td>
                            </tr>
                            <tr>
                                <td class="font-bold">Unit Kerja</td>
                                <td>:</td>
                                <td>{{ $page.props.selected_role?.unit_id || 'N/A' }}</td>
                            </tr>
                        </table>
                        <p>Tanggal: {{ new Date().toLocaleDateString('id-ID', {day: 'numeric', month: 'long', year: 'numeric'}) }}</p>
                    </div>
                </div>
                <template v-if="hasProposed">
                    <div class="mb-3">
                        <label class="block text-sm font-medium text-gray-700 mb-2">Alasan Pembatalan Anda</label>
                        <div class="border rounded-lg p-3 bg-gray-50 whitespace-pre-line">
                            {{ selectedDokumenPengadaan?.current_actor_propose?.alasan_pembatalan || '-' }}
                        </div>
                    </div>
                    <div class="mb-3">
                        <label class="block text-sm font-medium text-gray-700 mb-2">Tanda Tangan Anda</label>
                        <div class="border rounded-lg p-2 bg-gray-50 flex justify-center">
                            <img :src="selectedDokumenPengadaan?.current_actor_propose?.signature_url" alt="Signature" style="max-width: 100%; max-height: 260px;" v-if="selectedDokumenPengadaan?.current_actor_propose?.signature_url" />
                            <span v-else>-</span>
                        </div>
                    </div>
                    <div class="p-2 text-green-700 bg-green-50 border border-green-200 rounded text-sm">
                        <i class="pi pi-check mr-1"></i> Anda sudah mengusulkan pembatalan untuk dokumen ini.
                    </div>
                </template>
                <template v-else>
                    <div class="mb-3">
                        <label class="block text-sm font-medium text-gray-700 mb-2">Alasan Pembatalan</label>
                        <Textarea v-model="proposeForm.alasan" rows="4" placeholder="Jelaskan alasan pembatalan" />
                    </div>
                    <div class="mb-3">
                        <label class="block text-sm font-medium text-gray-700 mb-2">Tanda Tangan</label>
                        <div class="border rounded-lg p-2 bg-gray-100">
                            <VueSignaturePad class="border-2 rounded-lg p-2 bg-white"
                                ref="proposeSignaturePad"
                                width="500px" height="250px"
                                :options="signaturePadOptions" />
                        </div>
                        <div class="mt-2">
                            <Button label="Hapus Tanda Tangan" class="p-button-text" @click="clearProposeSignature" />
                        </div>
                    </div>
                </template>
                <div class="text-sm text-gray-600" v-if="selectedDokumenPengadaan?.pokja_summary">
                    Pokja setuju: {{ selectedDokumenPengadaan.pokja_summary.approved_count }} / {{ selectedDokumenPengadaan.pokja_summary.pokja_count }}
                </div>
            </div>
            <template #footer>
                <div class="flex justify-end gap-2">
                    <Button class="p-button-text" label="Tutup" @click="closeProposeDialog" />
                    <Button v-if="!hasProposed" class="p-button-warning" :disabled="!isProposeValid || submittingPropose" :loading="submittingPropose" label="Kirim Propose" @click="submitProposePembatalan" />
                </div>
            </template>
        </Dialog>
    </div>
</template>

<script>
import AppLayout from "../../Layouts/AppLayout";
import DataTable from "primevue/datatable";
import { FilterMatchMode } from "primevue/api";
import Column from "primevue/column";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import { Link } from '@inertiajs/inertia-vue3';
import OverlayPanel from 'primevue/overlaypanel';
import Dialog from 'primevue/dialog';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import Calendar from 'primevue/calendar';
import Textarea from 'primevue/textarea';
import { VueSignaturePad } from 'vue-signature-pad';

export default {
    name: "DokumenPengadaanBerjalan",
    layout: AppLayout,
    components: {
        AppLayout,
        DataTable,
        Column,
        Button,
        Link,
        InputText,
        OverlayPanel,
        Dialog,
        Toast,
        Calendar,
        Textarea,
        VueSignaturePad
    },
    setup() {
        const toast = useToast();
        return { toast };
    },
    computed: {
        isPokja() {
            const role = this.$page.props.session_role?.role?.name;
            return role === 'pokja';
        },
        hasProposed() {
            const state = this.selectedDokumenPengadaan?.current_actor_propose;
            return !!(state && state.is_setuju_pembatalan);
        },
        padOptions() {
            return {
                ...this.signaturePadOptions,
                onEnd: this.onSignatureEnd,
            }
        },
        isFormValid() {
            return this.catatan && 
                this.catatan.trim().length > 0 && 
                !this.signatureIsEmpty;
        },
        batalHeader() {
            const count = this.selectedDokumenPengadaan?.pokja_summary?.pokja_count || 0;
            if (count > 1) {
                return 'Konfirmasi Pembatalan — melakukan aksi pembatalan mewakili seluruh pokja dan seluruh pokja sudah setuju membatalkan';
            }
            return 'Konfirmasi Pembatalan';
        },
        isProposeValid() {
            return this.proposeForm.alasan && this.proposeForm.alasan.trim().length > 0;
        }
    },
    data() {
        return {
            loading: false,
            items: {
                data: [],
                total: 0
            },
            filters: {
                'global': { value: null, matchMode: FilterMatchMode.CONTAINS }
            },
            lazyParams: {
                first: 0,
                rows: 10,
                page: 1,
                search: ''
            },
            jadwalDialog: false,
            selectedJadwal: [],
            loadingJadwal: false,
            decryptDialog: false,
            decryptLoading: false,
            selectedDocumentId: null,
            selectedDocumentUnlockTime: null,
            // Edit Jadwal modal state
            editJadwalModalOpen: false,
            loadingEditJadwal: false,
            submittingEditJadwal: false,
            selectedEditDokumenId: null,
            editJadwalList: [],
            selectedJadwalId: null,
            editJadwalForm: null,
            isActiveEditJadwal: false,
            isExtendSelesaiOnlyJadwal: false,
            // Batal dialog properties
            konfirmBatalDialog: false,
            submittingBatal: false,
            selectedDokumenPengadaan: null,
            catatan: null,
            signatureIsEmpty: true,        
            signaturePadOptions: {
                penColor: 'black',
                backgroundColor: 'rgba(255,255,255,0)',
                minWidth: 1.2,
                throttle: 16,
            },
            // Propose dialog
            proposeDialog: false,
            submittingPropose: false,
            proposeForm: {
                alasan: ''
            },
        }
    },
    mounted() {
        this.loadData();
    },
    methods: {
        async loadData() {
            this.loading = true;
            try {
                const response = await axios.get(route('dokumen_pengadaan.berjalan.data'), {
                    params: {
                        page: this.lazyParams.page,
                        per_page: this.lazyParams.rows,
                        search: this.filters.global.value
                    }
                });
                this.items = response.data.payload;
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                this.loading = false;
            }
        },
        onPage(event) {
            this.lazyParams = {
                ...this.lazyParams,
                first: event.first,
                rows: event.rows,
                page: event.page + 1
            };
            this.loadData();
        },
        clearFilter() {
            this.filters = {
                'global': { value: null, matchMode: FilterMatchMode.CONTAINS }
            };
            this.loadData();
        },
        async showJadwalPopover(dokumenId) {
            this.loadingJadwal = true;
            this.jadwalDialog = true;
            try {
                const response = await axios.get(route('dokumen_pengadaan.jadwal', dokumenId));
                const jadwalData = response.data.data || [];
                
                // Add isActive flag to each jadwal based on current date/time
                const currentDateTime = new Date('2025-04-29T07:51:46+07:00'); // Use current time from metadata
                
                this.selectedJadwal = jadwalData.map(jadwal => {
                    const startDate = new Date(jadwal.waktu_mulai);
                    const endDate = new Date(jadwal.waktu_selesai);
                    const isActive = (currentDateTime >= startDate && currentDateTime <= endDate);
                    console.log('Jadwal:', this.formatJadwalName(jadwal.kode_jadwal), 
                                'Start:', startDate, 
                                'End:', endDate, 
                                'Current:', currentDateTime,
                                'Active:', isActive);
                    return {
                        ...jadwal,
                        isActive: isActive
                    };
                });
                
                console.log('Loaded jadwal:', this.selectedJadwal);
            } catch (error) {
                console.error('Error fetching jadwal:', error);
                this.toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Gagal memuat jadwal',
                    life: 3000
                });
            } finally {
                this.loadingJadwal = false;
            }
        },
        
        showSealedWarning() {
            this.toast.add({
                severity: 'warn',
                summary: 'Dokumen Tersegel',
                detail: 'Dokumen masih tersegel dan tidak dapat diakses',
                life: 3000
            });
        },
        
        formatJadwalName(kodeJadwal) {
            if (!kodeJadwal) return '';
            
            // Replace underscores with spaces and capitalize each word
            return kodeJadwal
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        },
        formatJadwalDisplay(j) {
            return j.nama_jadwal ? j.nama_jadwal : this.formatJadwalName(j.kode_jadwal);
        },

        confirmDecrypt(dokumenId) {
            this.selectedDocumentId = dokumenId;
            
            // Find the document to get its unlock_at time
            const document = this.items.data.find(doc => doc.id === dokumenId);
            if (document && document.unlock_at) {
                this.selectedDocumentUnlockTime = new Date(document.unlock_at).toLocaleString();
            } else {
                this.selectedDocumentUnlockTime = 'Tidak diketahui';
            }
            
            this.decryptDialog = true;
        },

        async decryptDocument() {
            if (!this.selectedDocumentId) return;
            
            this.decryptLoading = true;
            try {
                const response = await axios.post(route('dokumen_pengadaan.decrypt'), {
                    dokumen_id: this.selectedDocumentId
                });
                
                this.toast.add({
                    severity: 'success',
                    summary: 'Berhasil',
                    detail: 'Dokumen berhasil dibuka',
                    life: 3000
                });
                
                // Close dialog and refresh data
                this.decryptDialog = false;
                this.loadData();
                
            } catch (error) {
                console.error('Error decrypting document:', error);
                
                const errorMessage = error.response?.data?.message || 'Gagal membuka segel dokumen';
                this.toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: errorMessage,
                    life: 5000
                });
            } finally {
                this.decryptLoading = false;
            }
        },
        async openEditJadwalModal(dokumenId) {
            this.selectedEditDokumenId = dokumenId;
            this.editJadwalModalOpen = true;
            this.loadingEditJadwal = true;
            this.selectedJadwalId = null;
            this.editJadwalForm = null;
            this.isActiveEditJadwal = false;
            try {
                const response = await axios.get(route('dokumen_pengadaan.jadwal', dokumenId));
                this.editJadwalList = (response.data.data || []).map(jadwal => ({
                    ...jadwal,
                    label: jadwal.kode_jadwal_label || jadwal.nama || jadwal.kode_jadwal || 'Jadwal',
                }));
            } catch (error) {
                this.toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.response?.data?.message || 'Gagal memuat daftar jadwal',
                    life: 3000
                });
                this.editJadwalList = [];
            } finally {
                this.loadingEditJadwal = false;
            }
        },
        closeEditJadwalModal() {
            this.editJadwalModalOpen = false;
            this.selectedEditDokumenId = null;
            this.selectedJadwalId = null;
            this.editJadwalForm = null;
            this.isActiveEditJadwal = false;
            this.isExtendSelesaiOnlyJadwal = false;
        },
        onEditJadwalSelected() {
            if (!this.selectedJadwalId) return;
            const selectedJadwal = this.editJadwalList.find(j => j.id === this.selectedJadwalId);
            if (!selectedJadwal) return;
            
            // Get config for extend-selesai-only jadwals (from config/pengadaan.php)
            const extendSelesaiOnlyJadwals = ['upload_dokumen_penawaran']; // kode_jadwal_extend_selesai_only
            
            // Check if this jadwal is in the extend-selesai-only list
            this.isExtendSelesaiOnlyJadwal = extendSelesaiOnlyJadwals.includes(selectedJadwal.kode_jadwal);
            
            // For extend-selesai-only jadwals, check if waktu_selesai is in the past
            if (this.isExtendSelesaiOnlyJadwal) {
                const now = new Date();
                const waktuSelesai = selectedJadwal.waktu_selesai ? new Date(selectedJadwal.waktu_selesai) : null;
                
                if (waktuSelesai && waktuSelesai < now) {
                    this.toast.add({
                        severity: 'warn',
                        summary: 'Peringatan',
                        detail: 'Jadwal ini tidak dapat diedit karena waktu selesai sudah berlalu',
                        life: 3000
                    });
                    this.selectedJadwalId = null;
                    return;
                }
            }
            
            this.editJadwalForm = {
                waktu_mulai: selectedJadwal.waktu_mulai ? new Date(selectedJadwal.waktu_mulai) : null,
                waktu_selesai: selectedJadwal.waktu_selesai ? new Date(selectedJadwal.waktu_selesai) : null,
                tempat: selectedJadwal.tempat || '',
                keterangan: selectedJadwal.keterangan || ''
            };
        },
        isJadwalInPast(jadwal) {
            // For extend-selesai-only jadwals, only check waktu_selesai
            const extendSelesaiOnlyJadwals = ['upload_dokumen_penawaran'];
            const isExtendOnly = extendSelesaiOnlyJadwals.includes(jadwal.kode_jadwal);
            
            if (isExtendOnly) {
                // Only check if waktu_selesai is in the past
                if (!jadwal.waktu_selesai) return false;
                const now = new Date();
                return new Date(jadwal.waktu_selesai) < now;
            }
            
            // For other jadwals, no restrictions - never mark as "in past"
            return false;
        },
        formatDateTime(dateTimeString) {
            if (!dateTimeString) return '-';
            const options = { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric',
                hour: '2-digit', 
                minute: '2-digit',
                hour12: false
            };
            return new Date(dateTimeString).toLocaleString('id-ID', options);
        },
        async submitEditJadwal() {
            try {
                this.submittingEditJadwal = true;
                const currentJadwal = this.editJadwalList.find(j => j.id === this.selectedJadwalId);
                if (!currentJadwal) throw new Error('Jadwal tidak ditemukan');
                
                const currentWaktuSelesai = new Date(currentJadwal.waktu_selesai);
                
                // For extend-selesai-only jadwals, validate that waktu_selesai is later than current
                if (this.isExtendSelesaiOnlyJadwal) {
                    if (this.editJadwalForm.waktu_selesai <= currentWaktuSelesai) {
                        throw new Error('Waktu selesai baru harus lebih lama dari waktu selesai saat ini');
                    }
                }
                
                // Prepare form data
                const formData = {
                    pelaksanaan_jadwal_id: this.selectedJadwalId,
                    waktu_mulai: this.isExtendSelesaiOnlyJadwal 
                        ? this.formatDateForApi(currentJadwal.waktu_mulai)  // Keep original for extend-only
                        : this.formatDateForApi(this.editJadwalForm.waktu_mulai),  // Allow change for others
                    waktu_selesai: this.formatDateForApi(this.editJadwalForm.waktu_selesai),
                    tempat: this.editJadwalForm.tempat || '',
                    keterangan: this.editJadwalForm.keterangan || ''
                };
                
                const response = await axios.post(route('dokumen-pengadaan.edit-jadwal'), formData);
                this.toast.add({
                    severity: 'success',
                    summary: 'Berhasil',
                    detail: response.data.message,
                    life: 3000
                });
                this.closeEditJadwalModal();
                this.loadData();
            } catch (error) {
                this.toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.response?.data?.message || error.message || 'Terjadi kesalahan saat menyimpan jadwal',
                    life: 3000
                });
            } finally {
                this.submittingEditJadwal = false;
            }
        },
        formatDateForApi(dateInput) {
            if (!dateInput) return null;
            const dateObj = dateInput instanceof Date ? dateInput : new Date(dateInput);
            if (isNaN(dateObj.getTime())) return null;
            const pad = (num) => String(num).padStart(2, '0');
            const year = dateObj.getFullYear();
            const month = pad(dateObj.getMonth() + 1);
            const day = pad(dateObj.getDate());
            const hours = pad(dateObj.getHours());
            const minutes = pad(dateObj.getMinutes());
            const seconds = pad(dateObj.getSeconds());
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        },
        tryUnlock(document) {
            // Only allow unlock if current time >= unlock_at
            if (document.unlock_at) {
                const now = new Date();
                const unlockAt = new Date(document.unlock_at);
                if (now < unlockAt) {
                    this.toast.add({
                        severity: 'warn',
                        summary: 'Belum Waktunya',
                        detail: 'Dokumen hanya bisa dibuka setelah: ' + unlockAt.toLocaleString('id-ID'),
                        life: 4000
                    });
                    return;
                }
            }
            this.confirmDecrypt(document.id);
        },
        batal(dokumenPengadaan) {
            this.selectedDokumenPengadaan = dokumenPengadaan;
            this.konfirmBatalDialog = true;
            this.signatureIsEmpty = true;
            // Prefill alasan jika pokja > 1 dengan gabungan alasan yang sudah diproposes
            const combined = dokumenPengadaan?.pokja_summary?.alasan_combined;
            this.catatan = combined && combined.length ? combined : null;
            // Wait for dialog to render before resizing canvas
            this.$nextTick(() => {
                if (this.$refs.signaturePad) {
                    this.$refs.signaturePad.resizeCanvas();
                }
            });
        },
        openProposePembatalan(dokumenPengadaan) {
            this.selectedDokumenPengadaan = dokumenPengadaan;
            this.proposeDialog = true;
            this.proposeForm.alasan = '';
            this.$nextTick(() => {
                if (!this.hasProposed && this.$refs.proposeSignaturePad) {
                    this.$refs.proposeSignaturePad.resizeCanvas();
                    this.$refs.proposeSignaturePad.clearSignature();
                }
            });
        },
        closeProposeDialog() {
            this.proposeDialog = false;
            this.submittingPropose = false;
            this.proposeForm.alasan = '';
            this.selectedDokumenPengadaan = null;
        },
        clearProposeSignature() {
            this.$refs.proposeSignaturePad?.clearSignature();
        },
        tutupBatal() {
            this.konfirmBatalDialog = false;
            this.selectedDokumenPengadaan = null;
            this.signatureIsEmpty = true;
            this.catatan = null;
            this.$nextTick(() => {
                if (this.$refs.signaturePad) {
                    this.$refs.signaturePad.clearSignature();
                }
            });
        },
        onSignatureEnd() {
            this.signatureIsEmpty = this.$refs.signaturePad.isEmpty();
        },
        clearSignature() {
            this.$refs.signaturePad.clearSignature();
            this.signatureIsEmpty = true;
        },
        async confirmBatal() {
            if (!this.isFormValid || !this.selectedDokumenPengadaan) return;
            
            this.submittingBatal = true;

            try {
                const { isEmpty, data } = this.$refs.signaturePad.saveSignature();

                // Convert Base64 to Blob/File
                const res = await fetch(data);
                const blob = await res.blob();
                const file = new File([blob], `signature-${this.selectedDokumenPengadaan.id}-${Date.now()}.png`, {
                    type: blob.type
                });

                // Create FormData
                const form = new FormData();
                form.append('signature', file);
                form.append('catatan', this.catatan);
                form.append('eula', 1);
                
                const response = await axios.post(route('dokumen-pengadaan.batal-proses', this.selectedDokumenPengadaan.id), form);
                
                this.toast.add({
                    severity: 'success',
                    summary: 'Berhasil',
                    detail: response.data.message,
                    life: 3000,
                });
                
                this.tutupBatal();
                this.loadData(); // Refresh the data
                
            } catch (error) {
                console.error('Error detail:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status
                });
                
                if (error.response?.status === 422) {
                    this.toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.response.data.message,
                        life: 3000,
                    });
                } else {
                    this.toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Terjadi kesalahan saat membatalkan dokumen pengadaan',
                        life: 3000,
                    });
                }
            } finally {
                this.submittingBatal = false;
            }
        },
        async submitProposePembatalan() {
            if (!this.isProposeValid || !this.selectedDokumenPengadaan) return;
            this.submittingPropose = true;
            try {
                const { isEmpty, data } = this.$refs.proposeSignaturePad.saveSignature();
                if (isEmpty) {
                    this.toast.add({ severity: 'warn', summary: 'Perhatian', detail: 'Tanda tangan belum diisi', life: 3000 });
                    this.submittingPropose = false;
                    return;
                }
                const payload = {
                    alasan_pembatalan: this.proposeForm.alasan,
                    signature_pembatalan: data,
                };
                const response = await axios.post(route('dokumen-pengadaan.propose-pembatalan', this.selectedDokumenPengadaan.id), payload);
                this.toast.add({ severity: 'success', summary: 'Berhasil', detail: response.data.message || 'Usulan pembatalan tersimpan', life: 3000 });
                this.closeProposeDialog();
                await this.loadData();
            } catch (error) {
                this.toast.add({ severity: 'error', summary: 'Error', detail: error.response?.data?.message || 'Gagal mengusulkan pembatalan', life: 4000 });
            } finally {
                this.submittingPropose = false;
            }
        },
        async showHistoricEvaluasi(dokumenPengadaan) {
            try {
                // Fetch the latest riwayat evaluasi
                const response = await axios.get(route('riwayat-evaluasi.latest', dokumenPengadaan.id));
                if (response.data.success && response.data.data) {
                    // Navigate to the view page
                    this.$inertia.visit(route('riwayat-evaluasi.view', {
                        dokumenPengadaanId: dokumenPengadaan.id,
                        riwayatId: response.data.data.id
                    }));
                } else {
                    this.toast.add({
                        severity: 'info',
                        summary: 'Info',
                        detail: 'Tidak ada riwayat evaluasi ulang untuk dokumen ini',
                        life: 3000
                    });
                }
            } catch (error) {
                console.error('Error fetching historic evaluasi:', error);
                this.toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.response?.data?.message || 'Gagal memuat riwayat evaluasi',
                    life: 3000
                });
            }
        },
    },
    watch: {
        'filters.global.value'(newValue) {
            this.lazyParams.page = 1;
            this.lazyParams.search = newValue;
            this.loadData();
        }
    }
}
</script>

<style scoped>
.table-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.table-header h5 {
    margin: 0;
}

.active-jadwal {
    background-color: #b9fbc0 !important;
}

.p-datatable-row:nth-child(even) {
    background-color: rgba(0, 0, 0, 0.05);
}
</style>
